import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  Button,
  Descriptions,
  Empty,
  Spin,
  Table,
  Tag,
} from 'antd';
import { BackButton, PageContainer } from '@/ui/components';
import { useTranslate } from '@/translations';
import { useProductImageryQuery } from '@/api/ProductImages/hooks';
import { productImages } from '@/api/ProductImages/ProductImages';
import {
  ImageryAccess,
  ImageryError,
  ImageryStatus,
  imageryRoot,
  activeGeneration,
} from './ProductImages';
import { ImageryContent } from './ProductImages.styled';
import { PrivateImage } from './PrivateImage';
import { ImageQualityTag } from './ImageQualityTag';
import { VariantImages } from '@/modules/ProductImages/VariantImage';
import {
  generationOutput,
  generationStyleMessage,
  generationVariantKey,
  activeVariantGeneration,
  matchingVariantGeneration,
} from '@/api/ProductImages/generationVariants';
import { GenerationAudit } from '@/modules/ProductImages/GenerationAudit';
import {
  CandidateIdentityAssessment,
  ResearchIdentityStatus,
  storedEvaluationChecks,
} from '@/modules/ProductImages/IdentityAssessment';
import { variantImageMessage } from '@/modules/ProductImages/variantImageMessages';
import { ImageSuspensionNotice } from '@/modules/ProductImages/ImageSuspensionNotice';

export const formatImageryTime = (value?: string | null) =>
  value && !Number.isNaN(Date.parse(value))
    ? `${new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(new Date(value))} UTC`
    : '-';
export const shortId = (value: string) => value.slice(0, 8).toUpperCase();
const objectValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const textValue = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : typeof value === 'boolean'
      ? value
        ? 'Yes'
        : 'No'
      : '-';
const strings = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
const safeUrl = (value: unknown) => {
  if (typeof value !== 'string') return undefined;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) &&
      !url.username &&
      !url.password
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
};

const EvidenceLinks = ({
  value,
  references,
}: {
  value: unknown;
  references?: unknown;
}) => {
  const evidence = Array.isArray(value) ? value.map(objectValue) : [];
  const urls = strings(references).filter(
    (url) => !evidence.some((item) => item.url === url),
  );
  return (
    <>
      {evidence.map((item, index) => (
        <div className="imagery-evidence" key={index}>
          {typeof item.excerpt === 'string' ? <p>{item.excerpt}</p> : null}
          {safeUrl(item.url) ? (
            <a
              href={safeUrl(item.url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {textValue(item.url)}
            </a>
          ) : null}
        </div>
      ))}
      {urls.filter(safeUrl).map((url) => (
        <div key={url}>
          <a href={safeUrl(url)} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        </div>
      ))}
      {!evidence.length && !urls.filter(safeUrl).length ? '-' : null}
    </>
  );
};

const ResearchEvidence = ({
  research,
}: {
  research?: Record<string, unknown> | null;
}) => {
  const { t } = useTranslate();
  if (!research) return <Empty description={t('imagery.notAvailable')} />;
  const mapping = objectValue(research.identity_evidence);
  const identity: (Record<string, unknown> & { field: string })[] = [
    'make',
    'model',
    'generation',
    'year',
    'body',
    'trim',
    'engine_type',
  ]
    .filter(
      (field) =>
        field !== 'generation' || typeof research.generation === 'object',
    )
    .map((field) => ({
      field: field.replace('_', ' '),
      ...objectValue(research[field]),
    }));
  const sources = Array.isArray(research.sources)
    ? research.sources.map(objectValue).filter((source) => safeUrl(source.url))
    : [];
  return (
    <>
      <Descriptions
        size="small"
        layout="vertical"
        column={{ xs: 1, sm: 2, lg: 3 }}
        items={[
          ...(typeof research.generation === 'string'
            ? [
                {
                  key: 'generation',
                  label: t('imagery.generation'),
                  children: textValue(research.generation),
                },
              ]
            : []),
          {
            key: 'quality',
            label: t('imagery.evidenceQuality'),
            children: textValue(research.overall_evidence_quality),
          },
          ...(research.public_identity_established != null
            ? [
                {
                  key: 'public',
                  label: t('imagery.publicIdentity'),
                  children: textValue(research.public_identity_established),
                },
              ]
            : []),
        ]}
      />
      {typeof research.summary === 'string' ? (
        <p className="mt-3">{research.summary}</p>
      ) : null}
      <Table
        className="mt-3"
        size="small"
        rowKey="field"
        pagination={false}
        dataSource={identity}
        tableLayout="fixed"
        scroll={{ x: 850 }}
        columns={[
          {
            title: t('imagery.productInformation'),
            dataIndex: 'field',
            width: 130,
          },
          {
            title: t('imagery.result'),
            dataIndex: 'value',
            width: 180,
            render: textValue,
          },
          {
            title: t('imagery.status'),
            dataIndex: 'status',
            width: 110,
            render: textValue,
          },
          {
            title: t('imagery.confidence'),
            dataIndex: 'confidence',
            width: 110,
            render: (value) =>
              typeof value === 'number'
                ? `${Math.round(value * 100)}%`
                : t('imagery.unknown'),
          },
          {
            title: t('imagery.uncertainty'),
            dataIndex: 'uncertainty',
            width: 180,
            render: textValue,
          },
          {
            title: t('imagery.supportingEvidence'),
            width: 320,
            render: (_, field) => (
              <EvidenceLinks
                value={field.supporting_evidence}
                references={field.source_references}
              />
            ),
          },
        ]}
      />
      {Object.keys(mapping).length ? (
        <section className="mt-3">
          <h4>{t('imagery.identityMapping')}</h4>
          <Descriptions
            size="small"
            column={1}
            items={[
              {
                key: 'kind',
                label: t('imagery.mappingKind'),
                children: textValue(mapping.mapping_kind),
              },
              {
                key: 'code',
                label: t('imagery.modelCode'),
                children: textValue(mapping.code),
              },
              {
                key: 'market',
                label: t('imagery.applicableMarket'),
                children: textValue(mapping.applicable_market),
              },
              {
                key: 'mapping',
                label: t('imagery.mappingEvidence'),
                children: (
                  <EvidenceLinks
                    value={[
                      {
                        url: mapping.mapping_source_url,
                        excerpt: mapping.mapping_excerpt,
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'rule',
                label: t('imagery.mappingRule'),
                children: (
                  <EvidenceLinks
                    value={[
                      {
                        url: mapping.rule_source_url,
                        excerpt: mapping.rule_excerpt,
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </section>
      ) : null}
      {strings(research.warnings).length ? (
        <Alert
          className="mt-3"
          type="warning"
          message={t('imagery.warnings')}
          description={
            <ul className="mb-0">
              {strings(research.warnings).map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      {sources.length ? (
        <div className="mt-3">
          <h4 className="imagery-generation-subheading">
            {t('imagery.sources')}
          </h4>
          <ul>
            {sources.map((source, index) => (
              <li key={index}>
                <a
                  href={safeUrl(source.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {textValue(source.title || source.url)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
};

const GenerationRecord = () => {
  const { t } = useTranslate();
  const { generationId = '' } = useParams();
  const [params] = useSearchParams();
  const result = useProductImageryQuery(
    ['generation', generationId],
    () => productImages.generation(generationId),
    (record) =>
      activeGeneration(record.status) ||
      activeVariantGeneration(record.outputImage)
        ? 5_000
        : false,
  );
  if (result.isError)
    return (
      <ImageryError error={result.error} retry={() => void result.refetch()} />
    );
  if (!result.data) return <Spin />;
  const record = result.data;
  const selectedCandidate = record.candidates?.find(
    (candidate) => candidate.number === record.selectedCandidateNumber,
  );
  const productPath = `${imageryRoot}/products/${record.productId}?${params}`;
  const unknown = t('imagery.unknown');
  const isDerived = !!record.sourceRevisionId;
  const ownOutput = generationOutput(record);
  const variantKey = generationVariantKey(record);
  const styleMessage = generationStyleMessage(variantKey);
  const styleLabel = styleMessage ? t(styleMessage) : variantKey;
  const isOutline = record.outputImage?.representationKind === 'Outline';

  const outputLabel = isDerived
    ? styleLabel
    : t(
        isOutline || (!record.outputImage && record.variantKey === 'outline')
          ? 'imagery.outlineImage'
          : record.outputImage?.sourceKind === 'Generated'
            ? 'imagery.currentImage'
            : 'imagery.output',
      );
  return (
    <>
      <div className="imagery-generation-navigation">
        <BackButton title={t('imagery.product')} linkTo={productPath} />
        <Breadcrumb
          items={[
            {
              title: (
                <Link to={`${imageryRoot}?${params}`}>
                  {t('imagery.products')}
                </Link>
              ),
            },
            { title: <Link to={productPath}>{record.productCode}</Link> },
            { title: t('imagery.record') },
          ]}
        />
      </div>
      <header className="imagery-heading imagery-generation-heading">
        <div>
          <p className="imagery-generation-eyebrow">
            {t('imagery.record')}{' '}
            <span className="imagery-record-id">{shortId(record.id)}</span>
          </p>
          <h2>{record.productCode}</h2>
          <p className="imagery-generation-description">
            {record.productDescription}
          </p>
        </div>
        <div className="imagery-generation-header-actions">
          <div className="imagery-generation-tags">
            <Tag>{styleLabel}</Tag>
            <ImageryStatus status={record.status} />
            {record.status === 'Published' && record.outputRevisionId ? (
              <ImageQualityTag
                image={{
                  sourceKind: 'Generated',
                  qualityScore: selectedCandidate?.score,
                }}
              />
            ) : null}
          </div>
          <Button
            onClick={() => void result.refetch()}
            loading={result.isFetching}
          >
            {t('imagery.refresh')}
          </Button>
        </div>
      </header>
      {record.errorMessage || record.errorCode ? (
        <Alert
          className="imagery-generation-alert"
          type={record.status === 'Unknown' ? 'warning' : 'error'}
          showIcon
          message={variantImageMessage(
            t,
            record.errorCode || record.errorMessage,
          )}
          description={
            record.errorMessage &&
            record.errorMessage !==
              variantImageMessage(t, record.errorCode || record.errorMessage)
              ? record.errorMessage
              : undefined
          }
        />
      ) : null}
      <section className="imagery-generation-outputs">
        <h3>{t('imagery.generatedImages')}</h3>
        <div className="imagery-generation-results">
          <article className="imagery-generation-result">
            <div className="imagery-generation-result-heading">
              <h4>{outputLabel}</h4>
              {record.outputImage ? (
                <span className="imagery-caption">
                  {t('imagery.revision', {
                    revision: record.outputImage.revision,
                  })}
                </span>
              ) : null}
            </div>
            {isDerived && ownOutput ? (
              <PrivateImage
                src={ownOutput.contentUrl}
                generationId={record.id}
                label={outputLabel}
              />
            ) : !isDerived && record.outputRevisionId ? (
              <PrivateImage
                src={record.outputImage?.contentUrl}
                assetId={record.outputRevisionId}
                label={outputLabel}
              />
            ) : (
              <div className="imagery-image">
                <Empty description={t('imagery.noPublishedOutput')} />
              </div>
            )}
            <ImageSuspensionNotice image={record.outputImage} />
          </article>
          {isOutline && record.outputImage ? (
            <article className="imagery-generation-result">
              <div className="imagery-generation-result-heading">
                <h4>{t('imagery.variantResults')}</h4>
              </div>
              <VariantImages
                key={record.outputImage.assetId}
                image={record.outputImage}
                productId={record.productId}
                productCode={record.productCode}
                primaryActive={activeGeneration(record.status)}
              />
            </article>
          ) : null}
        </div>
      </section>
      <section className="imagery-section imagery-generation-panel">
        <h3>{t('imagery.generationSummary')}</h3>
        <Descriptions
          size="small"
          layout="vertical"
          column={{ xs: 1, sm: 2, lg: 3 }}
          items={[
            {
              key: 'style',
              label: t('imagery.style'),
              children: styleLabel,
            },
            {
              key: 'recipe',
              label: t('imagery.recipeVersion'),
              children: record.recipeVersion || '-',
            },
            ...(record.sourceRevisionId
              ? [
                  {
                    key: 'source',
                    label: t('imagery.sourceRevision'),
                    children: record.parentGenerationId ? (
                      <Link
                        to={`${imageryRoot}/generations/${record.parentGenerationId}?${params}`}
                      >
                        {record.sourceRevision == null
                          ? shortId(record.sourceRevisionId)
                          : t('imagery.revision', {
                              revision: record.sourceRevision,
                            })}
                      </Link>
                    ) : record.sourceRevision == null ? (
                      shortId(record.sourceRevisionId)
                    ) : (
                      t('imagery.revision', { revision: record.sourceRevision })
                    ),
                  },
                ]
              : []),
            {
              key: 'origin',
              label: t('imagery.origin'),
              children: record.triggerOrigin,
            },
            {
              key: 'by',
              label: t('imagery.requestedBy'),
              children: record.requestedBy || t('imagery.notAvailable'),
            },
            ...(selectedCandidate
              ? [
                  {
                    key: 'selected',
                    label: t('imagery.selectedCandidate'),
                    children: t('imagery.candidate', {
                      number: selectedCandidate.number,
                    }),
                  },
                ]
              : []),
            {
              key: 'requested',
              label: t('imagery.requestedAt'),
              children: formatImageryTime(record.requestedAt),
            },
            {
              key: 'started',
              label: t('imagery.startedAt'),
              children: formatImageryTime(record.startedAt),
            },
            {
              key: 'completed',
              label: t('imagery.completedAt'),
              children: formatImageryTime(record.completedAt),
            },
            {
              key: 'reason',
              label: t('imagery.reason'),
              children: record.reason || '-',
            },
            {
              key: 'step',
              label: t('imagery.currentStep'),
              children: record.currentStep || '-',
            },
            ...(record.batchId
              ? [
                  {
                    key: 'batch',
                    label: t('imagery.batch'),
                    children: (
                      <Link
                        to={`${imageryRoot}/batches/${record.batchId}?${params}`}
                      >
                        {shortId(record.batchId)}
                      </Link>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </section>
      {isDerived ? (
        <section className="imagery-section imagery-generation-panel">
          <h3>{t('imagery.checks')}</h3>
          <CandidateIdentityAssessment
            checks={storedEvaluationChecks(record.evaluation)}
            recipeVersion={record.recipeVersion}
          />
          <Descriptions
            size="small"
            layout="vertical"
            column={{ xs: 1, sm: 2, lg: 3 }}
            items={[
              {
                key: 'score',
                label: t('imagery.score'),
                children:
                  typeof record.evaluation?.score === 'number'
                    ? `${record.evaluation.score}/100`
                    : unknown,
              },
              {
                key: 'hardReject',
                label: t('imagery.hardRejection'),
                children: textValue(record.evaluation?.hardReject),
              },
              {
                key: 'reason',
                label: t('imagery.resultReason'),
                children: textValue(record.evaluation?.rejectionReason),
              },
              {
                key: 'error',
                label: t('imagery.errorCode'),
                children: record.errorCode || '-',
              },
            ]}
          />
        </section>
      ) : (
        <>
          <section className="imagery-section imagery-generation-panel">
            <h3>
              {t(
                isOutline ? 'imagery.outlineCandidates' : 'imagery.candidates',
              )}
            </h3>
            {record.candidates?.length ? (
              <div className="imagery-candidates">
                {record.candidates.slice(0, 2).map((candidate) => {
                  const selected =
                    candidate.number === record.selectedCandidateNumber;
                  const published =
                    selected &&
                    record.status === 'Published' &&
                    !!record.outputRevisionId;
                  const label = t(
                    published
                      ? 'imagery.publishedCandidate'
                      : 'imagery.unpublished',
                  );
                  return (
                    <article
                      className="imagery-candidate"
                      key={candidate.number}
                    >
                      <div className="imagery-generation-candidate-heading">
                        <div>
                          <h4>
                            {t('imagery.candidate', {
                              number: candidate.number,
                            })}
                          </h4>
                          <div className="imagery-generation-tags">
                            <Tag
                              color={
                                published || candidate.accepted
                                  ? 'success'
                                  : 'warning'
                              }
                            >
                              {t(
                                published
                                  ? 'imagery.published'
                                  : candidate.accepted
                                    ? 'imagery.accepted'
                                    : 'imagery.rejected',
                              )}
                            </Tag>
                            {selected && !published ? (
                              <Tag>{t('imagery.selected')}</Tag>
                            ) : null}
                            {published ? (
                              <ImageQualityTag
                                image={{
                                  sourceKind: 'Generated',
                                  qualityScore: candidate.score,
                                }}
                              />
                            ) : null}
                          </div>
                        </div>
                        <div className="imagery-generation-score">
                          <span>{t('imagery.score')}</span>
                          <strong>
                            {candidate.score == null
                              ? unknown
                              : `${candidate.score}/100`}
                          </strong>
                        </div>
                      </div>
                      {candidate.contentUrl ? (
                        <PrivateImage
                          src={candidate.contentUrl}
                          generationId={record.id}
                          candidate={candidate.number}
                          label={label}
                        />
                      ) : (
                        <Empty description={t('imagery.previewUnavailable')} />
                      )}
                      <p className="imagery-caption">{label}</p>
                      <CandidateIdentityAssessment
                        checks={candidate.checks}
                        recipeVersion={record.recipeVersion}
                      />
                      <Descriptions
                        className="mt-3"
                        size="small"
                        column={1}
                        items={[
                          {
                            key: 'reason',
                            label: t('imagery.resultReason'),
                            children: candidate.rejectionReason || '-',
                          },
                          {
                            key: 'hard',
                            label: t('imagery.hardRejection'),
                            children: textValue(candidate.checks?.hard_reject),
                          },
                          {
                            key: 'confidence',
                            label: t('imagery.confidence'),
                            children: textValue(candidate.checks?.confidence),
                          },
                          {
                            key: 'evaluation',
                            label: t('imagery.checks'),
                            children: textValue(candidate.checks?.reason),
                          },
                          {
                            key: 'issues',
                            label: t('imagery.issues'),
                            children:
                              strings(candidate.checks?.issue_codes).join(
                                ', ',
                              ) || '-',
                          },
                        ]}
                      />
                    </article>
                  );
                })}
              </div>
            ) : (
              <Empty
                description={
                  activeGeneration(record.status)
                    ? t('imagery.generating')
                    : t('imagery.notAvailable')
                }
              />
            )}
          </section>
          <section className="imagery-section imagery-generation-panel">
            <h3>{t('imagery.identitySummary')}</h3>
            <ResearchIdentityStatus
              research={record.research}
              recipeVersion={record.recipeVersion}
            />
            <ResearchEvidence research={record.research} />
          </section>
        </>
      )}
      <section className="imagery-section imagery-generation-panel">
        <h3>{t('imagery.usage')}</h3>
        <Descriptions
          size="small"
          layout="vertical"
          column={{ xs: 1, sm: 2, lg: 3 }}
          items={[
            {
              key: 'requests',
              label: t('imagery.modelRequests'),
              children: record.modelRequestCount ?? unknown,
            },
            ...(!isDerived
              ? [
                  {
                    key: 'web',
                    label: t('imagery.webActions'),
                    children: record.webActionCount ?? unknown,
                  },
                ]
              : []),
            {
              key: 'cost',
              label: t('imagery.estimatedCost'),
              children:
                record.estimatedCost == null
                  ? unknown
                  : `$${record.estimatedCost.toFixed(4)}`,
            },
          ]}
        />
        <Table
          className="mt-3"
          size="small"
          pagination={false}
          rowKey="rowKey"
          dataSource={(record.usage ?? []).map((usage, index) => ({
            ...usage,
            rowKey: index,
          }))}
          scroll={{ x: 700 }}
          locale={{ emptyText: t('imagery.notAvailable') }}
          columns={[
            { title: t('imagery.step'), dataIndex: 'step' },
            ...(!isDerived
              ? [
                  {
                    title: t('imagery.candidates'),
                    dataIndex: 'candidate',
                    render: (value: number | null) => value ?? '-',
                  },
                ]
              : []),
            {
              title: t('imagery.model'),
              dataIndex: 'model',
              render: (value) => value || unknown,
            },
            {
              title: t('imagery.inputTokens'),
              dataIndex: 'inputTokens',
              render: (value) => value ?? unknown,
            },
            {
              title: t('imagery.outputTokens'),
              dataIndex: 'outputTokens',
              render: (value) => value ?? unknown,
            },
            { title: t('imagery.result'), dataIndex: 'outcome' },
            {
              title: t('imagery.estimatedCost'),
              dataIndex: 'estimatedCost',
              render: (value) =>
                value == null ? unknown : `$${Number(value).toFixed(4)}`,
            },
          ]}
        />
      </section>
      <section className="imagery-section imagery-generation-panel">
        <h3>{t('imagery.stageDurations')}</h3>
        <Table
          size="small"
          pagination={false}
          rowKey="rowKey"
          scroll={{ x: 460 }}
          dataSource={(record.stageDurations ?? []).map((duration, index) => ({
            ...duration,
            rowKey: index,
          }))}
          locale={{ emptyText: t('imagery.notAvailable') }}
          columns={[
            { title: t('imagery.step'), dataIndex: 'step' },
            ...(!isDerived
              ? [
                  {
                    title: t('imagery.candidates'),
                    dataIndex: 'candidate',
                    render: (value: number | null) => value ?? '-',
                  },
                ]
              : []),
            {
              title: t('imagery.duration'),
              dataIndex: 'milliseconds',
              render: (value) =>
                typeof value === 'number'
                  ? `${(value / 1000).toFixed(1)} s`
                  : unknown,
            },
          ]}
        />
      </section>
      {isOutline && record.outputImage
        ? record.outputImage.variantResults?.map((result) => {
            const generation = matchingVariantGeneration(
              record.outputImage!,
              result,
            );
            const label = generationStyleMessage(result.variantKey);
            return generation ? (
              <section className="imagery-section" key={result.variantKey}>
                <h3>{label ? t(label) : result.variantKey}</h3>
                <GenerationAudit record={generation} layout="fullWidth" />
              </section>
            ) : null;
          })
        : null}
    </>
  );
};

export const GenerationRecordPage = () => (
  <ImageryAccess>
    <PageContainer>
      <ImageryContent className="imagery-generation-page">
        <GenerationRecord />
      </ImageryContent>
    </PageContainer>
  </ImageryAccess>
);
